import {useEffect, useMemo, useState} from 'react';
import {isEmpty} from 'lodash';

import {EVENT_NAMES} from '/enum/tealium.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

import {colorScaleByBonusId} from './Bonuses.style';
import {styleMutation} from './Bonuses.style';
import {UseBonusesProps} from './Bonuses.types';
import {getDateArray, getFormattedDatePeriods} from './Bonuses.util';

export const NO_VALUE = -1;

export const useBonuses = ({
  getBonuses = () => {}, // No-op function as default
  bonuses = {},
  onDateSelectorPress = () => {},
  currentScreen = '',
  lastPaidRank = '',
}: UseBonusesProps) => {
  const {tt} = useTranslation('bonusesPage');
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);
  const [selectedTileIndex, setSelectedTileIndex] = useState(NO_VALUE);
  const [externalMutations, setExternalMutations] = useState(undefined);

  const dateArray = useMemo(getDateArray, []);
  const formattedPeriodsForPiker: string[] = useMemo(
    () => getFormattedDatePeriods(dateArray, 'MMM d', 'MMM d, yyyy'),
    [dateArray],
  );
  const formattedPeriods: string[] = useMemo(
    () => getFormattedDatePeriods(dateArray, 'MMMM d', 'MMMM d, yyyy'),
    [dateArray],
  );
  const formattedCurrentPeriod: string =
    formattedPeriodsForPiker[selectedPeriodIndex] || '';
  // We need to use this to distinguish the lack of data for routing from an empty response.
  const currentBonuses = useMemo(() => {
    return bonuses ? bonuses[formattedCurrentPeriod] || null : null;
  }, [bonuses, formattedCurrentPeriod]);

  useEffect(() => {
    const dateUtil = getDateUtilDefaultInstance();
    // @ts-expect-error TS(2339): Property 'startDate' does not exist on type '{ sta... Remove this comment to see the full error message
    const {startDate} = dateArray[selectedPeriodIndex];

    // We are adding a day so that there are no problems with timezones.
    // Api supports any date from the period
    const secondDay = new Date(startDate);
    secondDay.setDate(secondDay.getDate() + 1);

    getBonuses({
      date: dateUtil.getEpoch(secondDay),
      formattedCurrentPeriod,
    });
    setSelectedTileIndex(NO_VALUE);
  }, [
    selectedPeriodIndex,
    formattedCurrentPeriod,
    dateArray,
    getBonuses,
    setSelectedTileIndex,
  ]);

  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.onPageLoad,
  });

  useEffect(() => {
    if (currentScreen !== 'performance') {
      sendEvent({
        currentScreenName: currentScreen,
        previousScreenName: 'performance',
        lastPaidRank: lastPaidRank,
      });
    }
  }, [currentScreen]);

  const totalBonus = useMemo(
    () =>
      !isEmpty(currentBonuses)
        ? // @ts-expect-error TS(7006): Parameter 'acc' implicitly has an 'any' type.
          currentBonuses?.reduce((acc, val) => ({
            bonusAmount: acc.bonusAmount + val.bonusAmount,
          })).bonusAmount
        : 0,
    [currentBonuses],
  );

  const onSelectorPress = () =>
    onDateSelectorPress({
      items: formattedPeriodsForPiker,
      selectedItemIndex: selectedPeriodIndex,
      setSelectedItemIndex: setSelectedPeriodIndex,
    });

  const removeMutation = () => setExternalMutations(undefined);

  const sortedColorScale = currentBonuses?.map(
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    ({bonusID}: any) => colorScaleByBonusId[bonusID],
  );

  const onTilePress = (index: any) => {
    const isSelected = index === selectedTileIndex;

    if (selectedTileIndex !== NO_VALUE) {
      // @ts-expect-error TS(2345): Argument of type '{ target: string[]; eventKey: st... Remove this comment to see the full error message
      setExternalMutations([
        {
          target: ['data'],
          eventKey: 'all',
          mutation: ({index}: any) =>
            isSelected && styleMutation({colorScale: sortedColorScale, index}),
          callback: removeMutation.bind(this),
        },
      ]);
    }
    setSelectedTileIndex(isSelected ? NO_VALUE : index);
  };

  return {
    tt,
    totalBonus,
    onTilePress,
    formattedPeriods,
    currentBonuses,
    selectedPeriodIndex,
    externalMutations,
    selectedTileIndex,
    onSelectorPress,
    sortedColorScale,
  };
};
